import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BookOpenIcon,
  AcademicCapIcon,
  CalendarIcon,
  IdentificationIcon,
  MapPinIcon,
  GlobeAltIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../api/client';
import { FireLoader } from '../components/Loader';
import {
  showSuccess,
  showError,
  showWarning,
  showConfirm,
} from '../utils/alert';

export default function EditTeacher() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(''); // single class ID
  const [currentTeacherClassId, setCurrentTeacherClassId] = useState(null);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    middle_name: '',
    date_of_birth: '',
    gender: '',
    nationality: 'Sierra Leonean',
    address: '',
    city: '',
    state: '',
    country: 'Sierra Leone',
    phone: '',
    email: '',
    qualification: '',
    teacher_status: '',
    pin_code: '',
    nin_number: '',
    class_taught: '',
    years_of_experience: 0,
    joining_date: '',
    status: 'active'
  });

  const fetchTeacherAndClasses = async () => {
    try {
      // Fetch teacher data
      const teacherRes = await apiClient.get(`/teachers/${id}`);
      const teacher = teacherRes.data.data || teacherRes.data;

      // Populate form data
      setFormData({
        first_name: teacher.first_name || '',
        last_name: teacher.last_name || '',
        middle_name: teacher.middle_name || '',
        date_of_birth: teacher.date_of_birth || '',
        gender: teacher.gender || '',
        nationality: teacher.nationality || 'Sierra Leonean',
        address: teacher.address || '',
        city: teacher.city || '',
        state: teacher.state || '',
        country: teacher.country || 'Sierra Leone',
        phone: teacher.phone || '',
        email: teacher.email || '',
        qualification: teacher.qualification || '',
        teacher_status: teacher.teacher_status || '',
        pin_code: teacher.pin_code || '',
        nin_number: teacher.nin_number || '',
        class_taught: teacher.class_taught || '',
        years_of_experience: teacher.years_of_experience || 0,
        joining_date: teacher.joining_date || '',
        status: teacher.status || 'active'
      });

      // Get current class ID from teacher data
      const currentClassId = teacher.current_class_id || (teacher.classes && teacher.classes.length > 0 ? teacher.classes[0].id : null);
      setCurrentTeacherClassId(currentClassId);
      setSelectedClassId(currentClassId ? String(currentClassId) : '');

      // Fetch all classes
      const classesRes = await apiClient.get('/classes', {
        params: { limit: 100, status: 'active' }
      });
      let data = [];
      if (classesRes.data) {
        if (Array.isArray(classesRes.data)) {
          data = classesRes.data;
        } else if (classesRes.data.data) {
          data = classesRes.data.data;
        }
      }
      setClasses(data);
    } catch (error) {
      await showError({ text: 'Failed to fetch teacher data' });
      console.error('Error fetching teacher:', error);
      navigate('/app/teachers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeacherAndClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClassChange = (e) => {
    setSelectedClassId(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const submitData = {
        ...formData,
        class_id: selectedClassId ? parseInt(selectedClassId) : null,
      };

      // Remove empty fields
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '' || submitData[key] === null) {
          delete submitData[key];
        }
      });

      // Convert years_of_experience to number
      if (submitData.years_of_experience) {
        submitData.years_of_experience = parseInt(submitData.years_of_experience);
      }

      await apiClient.put(`/teachers/${id}`, submitData);
      await showSuccess({ text: 'Teacher updated successfully!' });
      navigate(`/app/teachers/${id}`);
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to update teacher';
      await showError({ text: msg });
      console.error('Error updating teacher:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const getClassLabel = (cls) => {
    let label = `${cls.name} (${cls.level}`;
    if (cls.faculty && cls.faculty !== 'general') {
      label += ` - ${cls.faculty}`;
    }
    label += ')';
    return label;
  };

  // Filter classes: show all classes that have no teacher,
  // plus the teacher's current class (if it has a teacher, it's the teacher themselves)
  const availableClasses = classes.filter(cls => {
    // If class has no class_teacher_id, it's available
    if (!cls.class_teacher_id) return true;
    // If class has a teacher but it's the current teacher, also show it
    if (cls.class_teacher_id === parseInt(id)) return true;
    // Otherwise, class already has a different teacher – hide it
    return false;
  });

  if (loading) {
    return <FireLoader fullScreen={true} size="lg" text="LOADING..." />;
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto animate-fade-in">
      <button
        onClick={() => navigate(`/app/teachers/${id}`)}
        className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 transition-colors"
      >
        <ArrowLeftIcon className="w-5 h-5" />
        Back to Teacher Details
      </button>

      <div className="bg-white rounded-2xl shadow-sm border border-primary-50/50 p-6">
        <h1 className="text-2xl font-bold text-primary-700 mb-6">Edit Teacher</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div className="border-b border-primary-100 pb-4">
            <h2 className="text-lg font-semibold text-primary-700 mb-4">Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-primary-600 mb-2">
                  First Name *
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-400" />
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-2">
                  Last Name *
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-400" />
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-2">
                  Middle Name
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-400" />
                  <input
                    type="text"
                    name="middle_name"
                    value={formData.middle_name}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-2">
                  Date of Birth
                </label>
                <div className="relative">
                  <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-400" />
                  <input
                    type="date"
                    name="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-2">
                  Gender
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700"
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-2">
                  Email
                </label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Professional Information */}
          <div className="border-b border-primary-100 pb-4">
            <h2 className="text-lg font-semibold text-primary-700 mb-4">Professional Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-primary-600 mb-2">
                  Qualification
                </label>
                <div className="relative">
                  <AcademicCapIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-400" />
                  <input
                    type="text"
                    name="qualification"
                    value={formData.qualification}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-2">
                  Teacher Status
                </label>
                <select
                  name="teacher_status"
                  value={formData.teacher_status}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700"
                >
                  <option value="">Select Status</option>
                  <option value="School Leader">School Leader</option>
                  <option value="Deputy">Deputy</option>
                  <option value="Senior Teacher">Senior Teacher</option>
                  <option value="Teacher">Teacher</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-2">
                  PIN Code
                </label>
                <div className="relative">
                  <IdentificationIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-400" />
                  <input
                    type="text"
                    name="pin_code"
                    value={formData.pin_code}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-2">
                  NIN Number
                </label>
                <div className="relative">
                  <IdentificationIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-400" />
                  <input
                    type="text"
                    name="nin_number"
                    value={formData.nin_number}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-2">
                  Class Taught
                </label>
                <div className="relative">
                  <BookOpenIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-400" />
                  <input
                    type="text"
                    name="class_taught"
                    value={formData.class_taught}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-2">
                  Years of Experience
                </label>
                <input
                  type="number"
                  name="years_of_experience"
                  value={formData.years_of_experience}
                  onChange={handleChange}
                  min="0"
                  max="60"
                  className="w-full px-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-2">
                  Joining Date
                </label>
                <div className="relative">
                  <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-400" />
                  <input
                    type="date"
                    name="joining_date"
                    value={formData.joining_date}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="on_leave">On Leave</option>
                  <option value="terminated">Terminated</option>
                </select>
              </div>

              {/* Class Teacher - Single Select */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-primary-600 mb-2">
                  Class Teacher
                  <span className="text-primary-400 text-xs ml-2">(Only one class – choose from available classes)</span>
                </label>
                <select
                  value={selectedClassId}
                  onChange={handleClassChange}
                  className="w-full px-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700"
                >
                  <option value="">Select a class (optional)</option>
                  {availableClasses.map(cls => (
                    <option key={cls.id} value={cls.id}>
                      {getClassLabel(cls)}
                      {cls.class_teacher_id && cls.class_teacher_id === parseInt(id) && ' (current)'}
                    </option>
                  ))}
                </select>
                {availableClasses.length === 0 && (
                  <p className="text-sm text-yellow-600 mt-2">
                    No available classes to assign. All classes already have a class teacher.
                  </p>
                )}
                <p className="text-xs text-primary-400 mt-1">
                  Only classes without a class teacher are shown, plus your current class (if any).
                </p>
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="border-b border-primary-100 pb-4">
            <h2 className="text-lg font-semibold text-primary-700 mb-4">Address Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-primary-600 mb-2">
                  Address
                </label>
                <div className="relative">
                  <MapPinIcon className="absolute left-3 top-3 w-5 h-5 text-primary-400" />
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows="2"
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-2">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-2">
                  State/Province
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-600 mb-2">
                  Country
                </label>
                <div className="relative">
                  <GlobeAltIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-400" />
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-primary-200/50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 outline-none bg-primary-50/30 text-primary-700"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate(`/app/teachers/${id}`)}
              className="px-6 py-2.5 border border-primary-200 rounded-xl hover:bg-primary-50 transition-colors text-primary-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  <CheckIcon className="w-5 h-5" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}